import React, { useState } from "react";
import hosys from "@/api/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Plus, Loader2 } from "lucide-react";
import moment from "moment";

const TRAINING_TYPES = { initial: "Inicial", periodic: "Periòdica", specific: "Específica", emergency: "Emergència", equipment: "Equip", other: "Altres" };

export default function SafetyTrainingList({ venue_id, trainings, onChanged, isAdmin }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ worker_name: "", worker_email: "", training_type: "initial", title: "", training_date: new Date().toISOString().split("T")[0], duration_hours: 1, status: "completed" });
  const [saving, setSaving] = useState(false);

  const openForm = (item = null) => {
    setEditing(item);
    setForm(item ? { ...form, ...item } : { worker_name: "", worker_email: "", training_type: "initial", title: "", training_date: new Date().toISOString().split("T")[0], duration_hours: 1, status: "completed", venue_id });
    setShowForm(true);
  };

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSave = async () => {
    if (!form.worker_name || !form.title) return;
    setSaving(true);
    if (editing?.id) {
      await hosys.entities.SafetyTraining.update(editing.id, { ...form, venue_id });
    } else {
      await hosys.entities.SafetyTraining.create({ ...form, venue_id });
    }
    setSaving(false);
    setShowForm(false);
    onChanged();
  };

  const handleDelete = async (id) => {
    await hosys.entities.SafetyTraining.delete(id);
    onChanged();
  };

  const statusColor = { completed: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", expired: "bg-red-100 text-red-700" };

  return (
    <div className="space-y-3">
      {isAdmin && (
        <Button onClick={() => openForm()} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
          <Plus className="w-4 h-4" /> Registrar formació
        </Button>
      )}

      {trainings.length === 0 ? (
        <Card className="p-8 text-center border-0 shadow-sm"><p className="text-gray-500">Cap formació registrada</p></Card>
      ) : (
        <div className="space-y-2">
          {trainings.map(t => (
            <Card key={t.id} className="p-4 border-0 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="font-semibold text-gray-900">{t.title}</h4>
                    <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{TRAINING_TYPES[t.training_type]}</Badge>
                    <Badge className={`border-0 text-[10px] ${statusColor[t.status]}`}>{t.status}</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{t.worker_name}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                    <span>Data: {moment(t.training_date).format("DD/MM/YYYY")}</span>
                    <span>Durada: {t.duration_hours}h</span>
                    {t.trainer && <span>Formador: {t.trainer}</span>}
                    {t.expiry_date && <span>Caduca: {moment(t.expiry_date).format("DD/MM/YYYY")}</span>}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-[#7B2D3F]" onClick={() => openForm(t)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-red-600" onClick={() => handleDelete(t.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={o => !o && setShowForm(false)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar formació" : "Registrar formació"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Treballador *</Label><Input value={form.worker_name} onChange={e => set("worker_name", e.target.value)} placeholder="Nom del treballador" /></div>
            <div><Label>Email treballador</Label><Input type="email" value={form.worker_email || ""} onChange={e => set("worker_email", e.target.value)} placeholder="email@..." /></div>
            <div><Label>Títol de la formació *</Label><Input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Ex: Seguretat en cuina" /></div>
            <div><Label>Tipus</Label><Select value={form.training_type} onValueChange={v => set("training_type", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(TRAINING_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data formació</Label><Input type="date" value={form.training_date} onChange={e => set("training_date", e.target.value)} /></div>
              <div><Label>Durada (hores)</Label><Input type="number" value={form.duration_hours} onChange={e => set("duration_hours", parseFloat(e.target.value) || 1)} min="0.5" step="0.5" /></div>
            </div>
            <div><Label>Formador/Entitat</Label><Input value={form.trainer || ""} onChange={e => set("trainer", e.target.value)} placeholder="Nom del formador" /></div>
            <div><Label>Data caducitat (si aplica)</Label><Input type="date" value={form.expiry_date || ""} onChange={e => set("expiry_date", e.target.value)} /></div>
            <div><Label>Estat</Label><Select value={form.status} onValueChange={v => set("status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="completed">Completada</SelectItem><SelectItem value="pending">Pendent</SelectItem><SelectItem value="expired">Caducada</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel·lar</Button>
            <Button onClick={handleSave} disabled={!form.worker_name || !form.title || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editing ? "Actualitzar" : "Registrar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}